import { useI18n } from '@muse/i18n'

import {
    CollectionMiniCardContainer,
    CollectionMiniCardSkeleton,
    useCollectionSuggestions,
} from '@features/collection'
import { MusehubBannerContainer } from '@features/musehub'
import { TryForFreeCardContainer } from '@features/try-for-free'

import { useCollectionScreenCollection } from '../store/useCollectionScreenCollection'
import { useCollectionScreenSuggestions } from '../store/useCollectionScreenSuggestions'
import {
    CollectionSuggestionsSection,
    type TCollectionSuggestionItem,
} from '../components/collection-suggestions-section/CollectionSuggestionsSection'

// Match audio-web `CollectionSuggestionsSkeleton`: one placeholder for "Listen next", several for
// "From other creators".
const LISTEN_NEXT_SKELETON_COUNT = 1
const OTHER_CREATORS_SKELETON_COUNT = 10

const skeletonItems = (count: number): TCollectionSuggestionItem[] =>
    Array.from({ length: count }, (_, index) => ({
        key: String(index),
        content: <CollectionMiniCardSkeleton />,
    }))

// Sidebar — audio-web `CollectionSuggestions`: try-for-free + MuseHub promo, then "Listen next"
// (the author's other collections) and "From other creators", both from the `suggestions/collection`
// endpoint. Fetched client-side after hydration — the loader carries only the collection itself.
export const CollectionSuggestionsContainer = () => {
    const { t } = useI18n()
    const { authorUsername, slug } = useCollectionScreenCollection()
    const loaderSuggestions = useCollectionScreenSuggestions()
    const { data, isLoading } = useCollectionSuggestions({
        username: authorUsername,
        slug,
        initialData: loaderSuggestions,
    })

    const authorCollections = data?.author ?? []
    const otherCollections = data?.others ?? []

    const listenNextItems: TCollectionSuggestionItem[] = isLoading
        ? skeletonItems(LISTEN_NEXT_SKELETON_COUNT)
        : authorCollections.map(collection => ({
              key: collection.id,
              content: <CollectionMiniCardContainer collection={collection} />,
          }))

    const otherItems: TCollectionSuggestionItem[] = isLoading
        ? skeletonItems(OTHER_CREATORS_SKELETON_COUNT)
        : otherCollections.map(collection => ({
              key: collection.id,
              content: <CollectionMiniCardContainer collection={collection} />,
          }))

    // audio-web `CollectionSuggestions`: "Listen next" is hidden when the author has no other
    // collections; "From other creators" is rendered unconditionally.
    const showListenNext = isLoading || authorCollections.length > 0

    return (
        <>
            <TryForFreeCardContainer event={{ name: 'collection_try_click', payload: {} }} />
            <MusehubBannerContainer banner="landrFxVoice" size="small" />
            {showListenNext ? (
                <CollectionSuggestionsSection
                    title={t('i18n.collectionListenNext')}
                    items={listenNextItems}
                    collapsible={false}
                />
            ) : null}
            <CollectionSuggestionsSection
                title={t('i18n.collectionFromOtherCreators')}
                items={otherItems}
            />
        </>
    )
}
